<template>
  <Teleport to="body">
    <div v-if="isVisible" class="fixed inset-0 z-50 flex h-screen flex-col">
      <Header :theme="theme" />

      <div class="flex flex-1 flex-col items-center bg-blue-lighter">
        <div
          class="mt-10 w-full !max-w-[768px] overflow-hidden bg-white p-0 pb-4 lg:my-20 lg:rounded-3xl lg:pb-[79px]"
        >
          <IntroBlock v-if="introBlock" :data="introBlock" class="mb-4 mt-5 lg:mt-[50px]" />

          <!-- Try Again Button -->
          <div class="flex justify-center pb-6">
            <Button
              :title="errorButtonText"
              class="px-6 py-2"
              color="blue"
              @click="handleTryAgain"
            />
          </div>
        </div>
      </div>

      <Footer :theme="theme" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { IntroBlock, Button } from 'refresh-ui'
import { useFormErrorModal } from '~/composables/useFormErrorModal'
import Header from '@/components/elements/SimpleHeader.vue'
import Footer from '@/components/elements/SimpleFooter.vue'
import { THEMES } from '~/libs/themes'

const theme = computed(() => {
  return THEMES.DEFAULT
})

const { t } = useI18n()
const { isVisible, errorTitle, errorMessage, errorButtonText, hideError } = useFormErrorModal()

// Hide body scroll when modal is open
watch(isVisible, visible => {
  if (visible) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Cleanup on unmount
onUnmounted(() => {
  document.body.style.overflow = ''
})

const processedMessage = computed(() => {
  let text = errorMessage.value || t('error.generic.contact')

  // Convert email addresses to clickable links
  text = text.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1" class="text-blue-600 hover:text-blue-800 underline">$1</a>',
  )

  // Convert phone numbers to clickable links (Swedish format)
  text = text.replace(
    /(\d{3}-\d{3}\s\d{3}\s\d{2})/g,
    '<a href="tel:$1" class="text-blue-600 hover:text-blue-800 underline">$1</a>',
  )

  return text
})

const introBlock = computed(() => {
  return {
    id: 'error-act',
    subtitle: t('error.generic.subtitle'),
    title: errorTitle.value || t('error.generic.title'),
    copy: processedMessage.value,
    position: 'center',
    link: undefined,
  }
})

const handleTryAgain = () => {
  hideError()
}
</script>
